import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { Types } from '@grandvisionhq/graphql';
import { useToast } from '@grandvisionhq/iris-ui';
import { useIntl } from '@grandvisionhq/state';
import '@grandvisionhq/utils';
import '../../context/PaymentsUI/context.js';
import { usePaypalClientConfiguration } from '../../context/Paypal/PaypalClientConfigurationContext.js';
import { useInitiatePaypalTransaction } from './use-initiate-paypal-transaction.js';

const useCreatePaypalPaymentEvents = ({ cartId , onPaymentAuthorization , onPaymentTokenReceive , hasSubscription , checkoutType , onClick  })=>{
    const { toast  } = useToast();
    const { getLabel  } = useIntl();
    const [initiatePaypalTransaction] = useInitiatePaypalTransaction({
        cartId,
        checkoutType: checkoutType || Types.CheckoutType.REGULAR
    });
    const paypalConfig = usePaypalClientConfiguration();
    const createOrder = React.useCallback(swcHelpers.asyncToGenerator(function*() {
        const { data , errors  } = yield initiatePaypalTransaction();
        const response = data === null || data === void 0 ? void 0 : data.initiatePaypalTransaction.response;
        if (errors || !response) {
            throw new Error('Could not initiate paypal transaction');
        }
        if (response.__typename !== 'PaypalOrderResponse') {
            throw new Error('Subscription not implemented for create order');
        }
        if (onPaymentTokenReceive) {
            if (response.token) {
                onPaymentTokenReceive(response.token);
            } else {
                throw new Error('Did not receive payment token');
            }
        }
        return response.id;
    }), [
        initiatePaypalTransaction,
        onPaymentTokenReceive
    ]);
    const createBillingAgreement = React.useCallback(swcHelpers.asyncToGenerator(function*() {
        const { data , errors  } = yield initiatePaypalTransaction();
        const response = data === null || data === void 0 ? void 0 : data.initiatePaypalTransaction.response;
        if (errors || !response) {
            throw new Error('Could not initiate paypal transaction');
        }
        if (response.__typename !== 'PaypalSubscriptionResponse') {
            throw new Error('Billing agreement not supported for regular orders');
        }
        return response.billingAgreementTokenId;
    }), [
        initiatePaypalTransaction
    ]);
    const onApprove = React.useCallback(function() {
        var _ref = // Disabling the rule because the onApprove type from react-paypal-js requires a promise
        // eslint-disable-next-line @typescript-eslint/require-await
        swcHelpers.asyncToGenerator(function*(data, actions) {
            try {
                // If we have a subscription we have address validation from checkout page
                if (!actions.order || hasSubscription) {
                    onPaymentAuthorization(data.billingToken);
                } else {
                    var ref, ref1, ref2, ref3;
                    const details = yield actions.order.get();
                    const shippingAddress = (ref = details.purchase_units[0]) === null || ref === void 0 ? void 0 : (ref1 = ref.shipping) === null || ref1 === void 0 ? void 0 : ref1.address;
                    const name = (ref2 = details.purchase_units[0]) === null || ref2 === void 0 ? void 0 : (ref3 = ref2.shipping) === null || ref3 === void 0 ? void 0 : ref3.name;
                    const { countryCode , postalCode , fullName , streetName , hasValidTextLength  } = (paypalConfig === null || paypalConfig === void 0 ? void 0 : paypalConfig.addressValidation) || {};
                    let isValidAddress = true;
                    if (!(countryCode === null || countryCode === void 0 ? void 0 : countryCode((shippingAddress === null || shippingAddress === void 0 ? void 0 : shippingAddress.country_code) || '')) || !(postalCode === null || postalCode === void 0 ? void 0 : postalCode((shippingAddress === null || shippingAddress === void 0 ? void 0 : shippingAddress.postal_code) || ''))) {
                        toast.error(getLabel('checkout.payment.paypal.invalidCountry'));
                        isValidAddress = false;
                    }
                    if (checkoutType === Types.CheckoutType.EXPRESS) {
                        if (streetName && !streetName((shippingAddress === null || shippingAddress === void 0 ? void 0 : shippingAddress.address_line_1) || '')) {
                            toast.error(getLabel('checkout.payment.paypal.street.error.invalid.characters'));
                            isValidAddress = false;
                        }
                        if (fullName && !fullName((name === null || name === void 0 ? void 0 : name.full_name) || '')) {
                            toast.error(getLabel('checkout.payment.paypal.street.error.fullname.characters'));
                            isValidAddress = false;
                        }
                        if (hasValidTextLength && !hasValidTextLength((shippingAddress === null || shippingAddress === void 0 ? void 0 : shippingAddress.address_line_1) || '', 'address_line_1')) {
                            toast.error(getLabel('checkout.payment.paypal.street.error.invalid.length'));
                            isValidAddress = false;
                        }
                        if (hasValidTextLength && !hasValidTextLength((name === null || name === void 0 ? void 0 : name.full_name) || '', 'fullName')) {
                            toast.error(getLabel('checkout.payment.paypal.fullname.error.invalid.length'));
                            isValidAddress = false;
                        }
                    }
                    if (isValidAddress) {
                        onPaymentAuthorization(data.orderID);
                    }
                }
            } catch (e) {
                console.error(e);
            }
        });
        return function(data, actions) {
            return _ref.apply(this, arguments);
        };
    }(), [
        hasSubscription,
        onPaymentAuthorization,
        paypalConfig === null || paypalConfig === void 0 ? void 0 : paypalConfig.addressValidation,
        toast,
        getLabel,
        checkoutType, 
    ]);
    return {
        onError: console.error,
        onApprove,
        createOrder: !hasSubscription ? createOrder : undefined,
        createBillingAgreement: hasSubscription ? createBillingAgreement : undefined,
        onClick
    };
};

export { useCreatePaypalPaymentEvents };
//# sourceMappingURL=use-create-paypal-payment-events.js.map
